"use client";

import Link from "next/link";
import {
  BookOpen,
  Check,
  ClipboardCheck,
  Code2,
  Lock,
  PanelLeftClose,
  PanelLeftOpen,
  UserRound,
} from "lucide-react";
import { useTranslations } from "next-intl";
import { formatNumber } from "@/lib/format-datetime";
import { cn } from "@/lib/utils";
import type { Locale } from "@/lib/i18n";
import type { LearnLesson, LearnModule } from "@/features/learn/api";

/** One heading of the lesson on screen, found in its body after render. */
export type LearnSection = { id: string; label: string };

/** A problems lesson is a text lesson whose address ends in `/problems`. */
export function lessonKind(lesson: Pick<LearnLesson, "type" | "href">): "read" | "problems" | "test" {
  if (lesson.type === "quiz") return "test";
  return lesson.href.endsWith("/problems") ? "problems" : "read";
}

const KIND_ICON = { read: BookOpen, problems: Code2, test: ClipboardCheck } as const;

/**
 * The reader's left rail (`LN-UI05`, canvas page "Lesson reader").
 *
 * The old reader had no sense of place: a 14,500 px lesson with no list of its
 * own sections and a syllabus that opened as a modal of all 18 modules. The
 * rail answers the three questions a reader asks on the way through, in the
 * order they ask them: where am I in this module, where am I in this lesson,
 * and what comes after this module. The full syllabus is still one click away.
 *
 * It is built from what the page already has, so it is the same for every
 * track: the module's lessons come from the syllabus the track route sends,
 * completion from the viewer read the track page uses, and the sections from
 * the rendered lesson's own `h2`s. When the editor is open the rail folds to a
 * 56 px strip, so the reading column keeps its width (decision 1ক).
 */
export function LearnReaderRail({
  locale,
  contentLang,
  moduleNumber,
  module,
  currentLessonId,
  completedLessonIds,
  sections,
  activeSectionId,
  nextModules,
  moduleCount,
  collapsed,
  canCollapse,
  onToggleCollapsed,
  onOpenSyllabus,
}: {
  locale: Locale;
  contentLang: "en" | "bn";
  moduleNumber: number;
  module: LearnModule;
  currentLessonId: string;
  completedLessonIds: readonly string[];
  sections: LearnSection[];
  activeSectionId: string | null;
  nextModules: Array<{ number: number; module: LearnModule }>;
  moduleCount: number;
  collapsed: boolean;
  /** Only while the editor is open: without it the rail has room to stay. */
  canCollapse: boolean;
  onToggleCollapsed: () => void;
  onOpenSyllabus: () => void;
}) {
  const t = useTranslations("learn.reader");
  const done = new Set(completedLessonIds);
  const pick = (row: { title_en: string; title_bn: string | null }) =>
    contentLang === "bn" && row.title_bn ? row.title_bn : row.title_en;
  const num = (n: number) => formatNumber(n, contentLang);
  const lessons = module.lessons;
  const doneCount = lessons.filter((row) => done.has(row.id)).length;
  const position = lessons.findIndex((row) => row.id === currentLessonId) + 1;

  if (collapsed) {
    return (
      <nav
        aria-label={t("railLabel")}
        className="flex h-full w-14 flex-col items-center gap-3.5 border-r border-neutral-100 bg-white pt-4"
      >
        <button
          aria-label={t("railExpand")}
          className="flex h-9 w-9 items-center justify-center rounded-[10px] border border-neutral-200 text-neutral-600 transition-colors hover:border-primary hover:text-primary-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          onClick={onToggleCollapsed}
          title={t("railExpand")}
          type="button"
        >
          <PanelLeftOpen aria-hidden className="h-4 w-4" />
        </button>
        {position > 0 ? (
          <span className="font-mono text-[11px] font-bold text-primary-ink">
            {num(position)}/{num(lessons.length)}
          </span>
        ) : null}
        <span aria-hidden className="flex flex-col gap-1">
          {lessons.map((row) => (
            <span
              className={cn(
                "h-3.5 w-1 rounded-sm",
                row.id === currentLessonId || done.has(row.id) ? "bg-primary" : "bg-neutral-200",
              )}
              key={row.id}
            />
          ))}
        </span>
      </nav>
    );
  }

  return (
    <nav
      aria-label={t("railLabel")}
      className="flex h-full w-72 flex-col overflow-y-auto border-r border-neutral-100 bg-white pb-6"
    >
      <div className="px-[18px] pb-3.5 pt-5">
        <div className="flex items-start justify-between gap-2">
          <p className="font-[family-name:var(--font-heading)] text-[11px] font-bold uppercase tracking-[0.14em] text-primary-ink">
            {t("railModule", { n: num(moduleNumber) })}
          </p>
          {canCollapse ? (
            <button
              aria-label={t("railCollapse")}
              className="-mr-1.5 -mt-1.5 flex h-7 w-7 items-center justify-center rounded-md text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              onClick={onToggleCollapsed}
              title={t("railCollapse")}
              type="button"
            >
              <PanelLeftClose aria-hidden className="h-4 w-4" />
            </button>
          ) : null}
        </div>
        <p className="mt-1.5 font-[family-name:var(--font-heading)] text-[15px] font-bold leading-snug text-neutral-900">
          {pick(module)}
        </p>
        <div
          aria-hidden
          className="mt-3 grid gap-[3px]"
          style={{ gridTemplateColumns: `repeat(${Math.max(lessons.length, 1)}, minmax(0, 1fr))` }}
        >
          {lessons.map((row) => (
            <span
              className={cn("h-1 rounded-sm", done.has(row.id) ? "bg-primary" : "bg-neutral-200")}
              key={row.id}
            />
          ))}
        </div>
        <p className="mt-2 text-xs text-neutral-500">
          {t("railDone", { done: num(doneCount), total: num(lessons.length) })}
        </p>
      </div>

      <ol className="flex flex-col gap-0.5 px-2">
        {lessons.map((row) => {
          const isCurrent = row.id === currentLessonId;
          const isDone = done.has(row.id);
          const Kind = KIND_ICON[lessonKind(row)];
          return (
            <li key={row.id}>
              <Link
                aria-current={isCurrent ? "page" : undefined}
                className={cn(
                  "flex items-start gap-2.5 rounded-[10px] px-2.5 py-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                  isCurrent ? "bg-orange-50" : "hover:bg-neutral-50",
                )}
                href={`/${locale}${row.href}`}
              >
                <span
                  aria-hidden
                  className={cn(
                    "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full",
                    isDone
                      ? "bg-primary text-white"
                      : isCurrent
                        ? "border-2 border-primary"
                        : "border-[1.5px] border-neutral-300",
                  )}
                >
                  {isDone ? <Check className="h-2.5 w-2.5" strokeWidth={3.5} /> : null}
                </span>
                <span
                  className={cn(
                    "min-w-0 flex-1 text-[13px] leading-[1.45]",
                    isCurrent ? "font-semibold text-neutral-900" : "text-neutral-600",
                  )}
                >
                  {pick(row)}
                  {isDone ? <span className="sr-only"> ({t("railLessonDone")})</span> : null}
                </span>
                <span aria-hidden className="flex items-center gap-1.5 pt-0.5 text-neutral-400">
                  {row.learnAccess === "pro" ? (
                    <Lock className="h-3 w-3" />
                  ) : row.learnAccess === "core" ? (
                    <UserRound className="h-3 w-3" />
                  ) : null}
                  <Kind className="h-[13px] w-[13px]" />
                </span>
              </Link>
              {isCurrent && sections.length > 0 ? (
                <ul className="mb-2 ml-9 mr-2.5 mt-0.5 border-l border-orange-100 pl-3">
                  {sections.map((section) => {
                    const active = section.id === activeSectionId;
                    return (
                      <li key={section.id}>
                        <a
                          aria-current={active ? "location" : undefined}
                          className={cn(
                            "flex items-center gap-2 py-1 text-[12.5px] leading-snug transition-colors hover:text-primary-ink",
                            active ? "font-semibold text-primary-ink" : "text-neutral-500",
                          )}
                          href={`#${section.id}`}
                        >
                          <span
                            aria-hidden
                            className={cn(
                              "h-1.5 w-1.5 shrink-0 rounded-full",
                              active ? "bg-primary" : "bg-neutral-300",
                            )}
                          />
                          <span className="min-w-0">{section.label}</span>
                        </a>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </li>
          );
        })}
      </ol>

      <div className="mx-[18px] mt-3.5 border-t border-neutral-100 pt-3">
        {nextModules.length > 0 ? (
          <>
            <p className="mb-1 text-[11px] font-bold uppercase tracking-[0.1em] text-neutral-400">
              {t("railNextModules")}
            </p>
            <ul className="-mx-2.5">
              {nextModules.map(({ number, module: next }) => {
                const first = next.lessons[0];
                const row = (
                  <>
                    <span className="font-mono text-[11px] font-semibold text-neutral-400">
                      {num(number).padStart(2, formatNumber(0, contentLang))}
                    </span>
                    <span className="min-w-0 flex-1">{pick(next)}</span>
                    {first ? null : (
                      <span className="shrink-0 text-[11px] text-neutral-400">{t("railBeingWritten")}</span>
                    )}
                  </>
                );
                return (
                  <li key={next.id}>
                    {first ? (
                      <Link
                        className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] text-neutral-600 transition-colors hover:bg-neutral-50 hover:text-neutral-900"
                        href={`/${locale}${first.href}`}
                      >
                        {row}
                      </Link>
                    ) : (
                      <span className="flex items-center gap-2.5 px-2.5 py-2 text-[13px] text-neutral-500">
                        {row}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </>
        ) : null}
        <button
          className="mt-1.5 text-[12.5px] font-semibold text-primary-ink hover:underline"
          onClick={onOpenSyllabus}
          type="button"
        >
          {t("railAllModules", { count: num(moduleCount) })}
        </button>
      </div>
    </nav>
  );
}
